import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useSubscribeAsync } from '@/features/borderCrossingRORO/onBoarding/subscribePublicForm/apis';
import { RegisterSubscribeForm } from '@/features/borderCrossingRORO/onBoarding/subscribePublicForm/types';

export const useSubscribeForm = () => {
  const navigate = useNavigate();
  const schema = useFormSchema();
  const [isSubscribeFormInError, setIsSubscribeFormInError] = useState<boolean>(false);

  const { mutate: subscribeAsync, isLoading: isSubscribePending, isSuccess: isSubscribeSuccess } = useSubscribeAsync();

  const { control, formState, handleSubmit, register } = useForm<RegisterSubscribeForm>({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const handleCancelTheRequest = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleSubmitSubscribe = handleSubmit((data: RegisterSubscribeForm) => {
    subscribeAsync(
      {
        data,
      },
      {
        onSuccess: () => {
          setIsSubscribeFormInError(false);
        },
        onError: () => {
          setIsSubscribeFormInError(true);
        },
      },
    );
  });

  const handleCloseToaster = useCallback(() => {
    setIsSubscribeFormInError(false);
  }, []);

  return {
    isSubscribeFormInError,
    isSubscribePending,
    isSubscribeSuccess,
    formState,
    control,
    register,
    handleCloseToaster,
    handleCancelTheRequest,
    handleSubmitSubscribe,
  };
};

const phoneRegExp = /^(\+?[1-9]\d{0,3}[-.\s]?)?(\(?\d{1,4}\)?[-.\s]?)?(\d{1,4}[-.\s]?){1,3}\d{1,4}$/;

function useFormSchema() {
  const { t } = useTranslation('borderCrossingRORO_onBoarding');

  return Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    city: Yup.string()
      .trim()
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    countryCodeId: Yup.string()
      .trim()
      .nullable()
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    street: Yup.string()
      .trim()
      .max(120, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 120 })),
    PostCode: Yup.string()
      .trim()
      .max(20, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 20 })),
    phoneNumber: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .matches(phoneRegExp, t('borderCrossingRORO.onBoarding.form.invalidPhoneNumber.errorMessage'))
      .max(15, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 15 })),
    companyEmail: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .email(t('borderCrossingRORO.onBoarding.form.invalidMail.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    contactFirstName: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    contactLastName: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    email: Yup.string()
      .trim()
      .required(t('borderCrossingRORO.onBoarding.form.fieldIsMandatory.errorMessage'))
      .email(t('borderCrossingRORO.onBoarding.form.invalidMail.errorMessage'))
      .max(60, t('borderCrossingRORO.onBoarding.form.maxLength.error.message', { maxLength: 60 })),
    termsAndContractValidated: Yup.boolean()
      .nullable()
      .required(t('borderCrossingRORO.onBoarding.form.isTermsAndConditionOfServiceAccepted.errorMessage'))
      .isTrue(t('borderCrossingRORO.onBoarding.form.isTermsAndConditionOfServiceAccepted.errorMessage')),
  });
}
